/**
 * Login Service
 * 
 * Phase 2-B: Session Cookie Authentication
 * - Added getMe() for session bootstrap on app load
 * - All auth endpoints now rely on HttpOnly session cookie
 */

import API from "../../api/api";


const callApi = async (endpoint) => {
    try {
        const response = await API.get(endpoint);
        if (response.status === 200) {
            return response?.data?.data;
        } else {
            return response?.data?.data;
        }
    } catch (error) {
        console.log(error);
        throw error;
    }
};

const LoginService = {
    /**
     * Login - POST auth/access
     * Backend sets HttpOnly session cookie on success
     */
    authAccess: async (data) => {
        return API.post("auth/access", JSON.stringify(data))
            .then((response) => response.data)
            .catch((error) => Promise.reject(error));
    },
    
    /**
     * Verify 2FA OTP - POST auth/verify-twofa-otp
     * Backend sets HttpOnly session cookie on success
     */
    verify2faOtp: async (data) => {
        return API.post("auth/verify-twofa-otp", JSON.stringify(data))
            .then((response) => response.data)
            .catch((error) => Promise.reject(error));
    },
    
    /**
     * Logout - POST auth/logout
     * Backend clears session cookie
     */
    logout: async () => {
        return API.post("auth/logout")
            .then((response) => response.data)
            .catch((error) => Promise.reject(error));
    },
    
    /**
     * Get current session user - GET auth/me
     * Used for session bootstrap on app load/refresh
     * Returns user data if session is valid, 401 if not
     * 
     * Phase 2-B: This is the SOURCE OF TRUTH for authentication state
     */
    getMe: async () => {
        return API.get("auth/me")
            .then((response) => response.data)
            .catch((error) => Promise.reject(error));
    },
    
    forgotPassword: async (data) => {
        return API.post("auth/forgot-password", JSON.stringify(data))
            .then((response) => response.data)
            .catch((error) => Promise.reject(error));
    },
    
    verifyForgotPassword: async (data) => {
        return API.post('auth/verify-forgot-password', data)
            .then((response) => response)
            .catch((error) => Promise.reject(error));
    },
    
    changeForgotPassword: async (data) => {
        return API.post('auth/update-password', data)
            .then((response) => response.data)
            .catch((error) => Promise.reject(error));
    },
    
    verifyEmail: async (username, adminUsername) => {
        return API.post(`auth/email-verification?encryptedUsername=${username}&encryptedAdminUsername=${adminUsername}`)
    },

    // ---- حملة إعادة التعيين الأمنية المؤقتة (مسار مستقل عن نسيان كلمة المرور القديم) ----
    // ملاحظة: معترض axios يُرجع استجابة كاملة عند 200 وجسم الخطأ مباشرةً عند 422،
    // لذا نوحّد الشكل هنا ليكون دائماً { status, data } كما يرسله الخلفية.
    securityResetVerify: async (data) => {
        const res = await API.post("auth/security-reset/verify", JSON.stringify(data));
        return (res && typeof res.status === "number") ? res.data : res;
    },

    securityResetAccount: async (data) => {
        const res = await API.post("auth/security-reset/account-password", JSON.stringify(data));
        return (res && typeof res.status === "number") ? res.data : res;
    },

    securityResetWallet: async (data) => {
        const res = await API.post("auth/security-reset/wallet-password", JSON.stringify(data));
        return (res && typeof res.status === "number") ? res.data : res;
    },

    // ---- رابط تعيين كلمتي المرور بعد التسجيل (onboarding) ----
    onboardingVerify: async (data) => {
        const res = await API.post("auth/onboarding/verify", JSON.stringify(data));
        return (res && typeof res.status === "number") ? res.data : res;
    },

    onboardingAccountPassword: async (data) => {
        const res = await API.post("auth/onboarding/account-password", JSON.stringify(data));
        return (res && typeof res.status === "number") ? res.data : res;
    },

    onboardingWalletPassword: async (data) => {
        const res = await API.post("auth/onboarding/wallet-password", JSON.stringify(data));
        return (res && typeof res.status === "number") ? res.data : res;
    },

    companyLogo: async () => {
        return callApi('auth/get-company-logo')
    },
    
    /**
     * Get EMS SSO link - GET auth/ems/link
     * Returns redirect URL for EMS with SSO code
     * Opens EMS in new tab via SSO
     * 
     * @returns {Promise<{redirectUrl: string}>}
     */
    getEmsLink: async () => {
        return API.get("auth/ems/link")
            .then((response) => response.data?.data || response.data)
            .catch((error) => Promise.reject(error));
    },

    /**
     * Get LMS SSO link - GET auth/lms/link
     * Returns redirect URL for LMS with SSO code
     * Opens LMS in new tab via SSO
     * 
     * @returns {Promise<{redirectUrl: string}>}
     */
    getLmsLink: async () => {
        return API.get("auth/lms/link")
            .then((response) => response.data?.data || response.data)
            .catch((error) => Promise.reject(error));
    }
}

export default LoginService
